import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import { Button, ButtonIcon, Selector, Textarea } from '..';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../global';
import { GLOBAL_ICONS } from '../../util/vars';
import { FieldArray, Formik } from 'formik';
import { ModalFooter } from '../modal/Modal';
import type { OptionType } from '../../util/types';
import type { GoalModalProps, GoalProps } from './types';

const APPLIES = [
  { label: 'School', value: 'school' },
  { label: 'Class', value: 'class' },
  { label: 'Year Group', value: 'yearGroup' },
  { label: 'Tag', value: 'tag' },
];

const AddEditGoalModal = ({ toggle, toggleConfirm, data, defaultGoals, yearGroups, schoolClasses, schoolTags, onSuccess, onError }: GoalModalProps): JSX.Element => {
  const { translate: t } = useI18n();

  const editMode = null != data;

  const handleDelete = () => {
    toggle(false);
    toggleConfirm(true);
  };

  const GOALS = defaultGoals?.map((goal) => ({
    label: t(`pages.ADMIN.IMPACT_REPORTS.GOALS.${goal.goal_key}`),
    value: goal.goal_key,
  })) || [];

  return (
    <Formik<GoalProps>
      initialValues={{
        goal_key: '',
        cohort: {
          id: null,
          name: null,
        },
        metrics: [],
        notes: '',
        ...(editMode ? data : {}),
      }}
      onSubmit={(formData) => {
        onSuccess?.(formData);
        toggle(false);
      }}
    >
      {({ handleSubmit, values, setFieldValue, errors, resetForm, isValid, touched }) => {

        const METRICS_COMPONENTS = defaultGoals?.find((goal) => goal.goal_key === values.goal_key)?.metrics || [];

        let cohortOptions: OptionType<number>[] | null = null;

        switch (values.cohort.name) {
          case 'yearGroup':
            cohortOptions = yearGroups.map((yearGroup) => ({
              label: yearGroup.name,
              value: yearGroup.id,
            }));
            break;
          case 'class':
            cohortOptions = schoolClasses.map((classItem) => ({
              label: classItem.name,
              value: classItem.id,
            }));
            break;
          case 'tag':
            cohortOptions = schoolTags.map((tag) => ({
              label: tag.abbreviation,
              value: tag.id,
            }));
            break;
          case 'school':
          default:
            cohortOptions = null;
            break;
        }

        return (
          <form onSubmit={handleSubmit}>
            <div className={'container space-y-6'}>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.GOAL')}</h3>
                <Selector
                  id={'goal_key'}
                  label={''}
                  hideLabel
                  isClearable={false}
                  labelStyle={'bold'}
                  options={GOALS}
                  onChange={(option: OptionType<string>) => setFieldValue('goal_key', option.value)}
                  value={GOALS.find((option) => option.value === values.goal_key) || null}
                  error={errors.goal_key}
                />
              </div>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.APPLIES_TO')}</h3>
                <div className={'flex space-x-4'}>
                  <div className={'flex-grow'}>
                    <Selector
                      id={'cohort.name'}
                      label={''}
                      hideLabel
                      isClearable={false}
                      options={APPLIES}
                      onChange={(option: OptionType<NonNullable<GoalProps['cohort']['name']>>) => {
                        setFieldValue('cohort.name', option.value);
                        setFieldValue('cohort.id', null);
                      }}
                      value={APPLIES.find((option) => option.value === values.cohort.name) || null}
                      error={errors.cohort?.name}
                    />
                  </div>
                  {[ 'class', 'tag', 'yearGroup' ].includes(values.cohort?.name ?? '') ? (
                    <div className={'w-2/3'}>
                      <Selector
                        id={'cohort.id'}
                        label={''}
                        placeholder={'Select...'}
                        hideLabel
                        isClearable={false}
                        options={cohortOptions}
                        onChange={(options: OptionType<string>[]) => setFieldValue('cohort.id', options.map((option) => option.value))}
                        value={
                          Array.isArray(values.cohort.id)
                            ? cohortOptions?.filter((option: OptionType<number>) => Array.isArray(values.cohort.id) && values.cohort.id.includes(option.value)) || []
                            : []
                        }
                        error={errors.cohort?.id}
                        isMulti
                        isDisabled={cohortOptions === null}
                      />
                    </div>) : null }
                </div>
              </div>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.IMPACT_METRIC')}</h3>
                <FieldArray name={'metrics'}>
                  {({ push, replace, remove }) => {
                    return (
                      <div className={'space-y-4'}>
                        {values.metrics.map((component, index) => (
                          <div
                            key={index}
                            className={'mt-2 flex items-center space-x-4'}
                          >
                            <div>
                              <span>
                                {t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                              </span>
                            </div>
                            <div className={'flex-grow w-44'}>
                              <Selector
                                id={`metrics[${index}]`}
                                label={t('pages.ADMIN.FORM.LABELS.METRIC_COMPONENT', { number: index + 1 })}
                                hideLabel
                                isClearable={false}
                                options={METRICS_COMPONENTS
                                  .filter((metricsComponent) => !values.metrics.some((option) => metricsComponent === option))
                                  .map((metricsComponent) => ({
                                    label: t(`pages.ADMIN.IMPACT_REPORTS.METRICS.${metricsComponent}`),
                                    value: metricsComponent,
                                  }))
                                }
                                onChange={(option: OptionType<number>) => replace(index, option.value)}
                                value={values.metrics[index]
                                  ? {
                                    label: t(`pages.ADMIN.IMPACT_REPORTS.METRICS.${values.metrics[index]}`),
                                    value: values.metrics[index],
                                  }
                                  : null}
                                error={errors.metrics?.[index]}
                              />
                            </div>
                            <ButtonIcon
                              type={'button'}
                              icon={GLOBAL_ICONS.delete}
                              size={'md'}
                              title={t('pages.ADMIN.FORM.LABELS.DELETE_METRIC_COMPONENT', { number: index + 1 })}
                              onClick={() => remove(index)}
                              asLink
                            />
                          </div>
                        ))}

                        <Button
                          type={'button'}
                          className={'w-full'}
                          isOutline

                          onClick={() => push(null)}
                          disabled={values.metrics.length >= METRICS_COMPONENTS.length}
                        >
                          {t('pages.ADMIN.FORM.LABELS.ADD_METRIC_COMPONENT')}
                        </Button>
                      </div>
                    );
                  }}
                </FieldArray>
                {(!isValid && Object.keys(errors).some((key) => touched[key as keyof GoalProps]))
                && (<FormGenericError />)}
              </div>
              <div className={'space-y-2'}>
                <h3 className={'font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.NOTES')}</h3>
                <Textarea
                  id={'notes'}
                  label={''}
                  hideLabel
                  placeholder={'Notes...'}
                  onChange={(event) => setFieldValue('notes', event.target.value)}
                  value={values.notes ?? ''}
                  error={errors.notes}
                />
              </div>
            </div>
            <ModalFooter justify={editMode ? 'between' : 'end'}>
              {editMode && (
                <CancelButtonModal
                  align={'start'}
                  onClick={() => resetForm()}
                >
                  {t('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.BUTTONS.DISCARD')}
                </CancelButtonModal>
              )}
              <SaveButton type={'submit'} />
            </ModalFooter>
            {editMode && (
              <div className={'container space-y-6'}>
                <h5 className={'font-bold'}>
                  {t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.DELETE_GOAL')}
                </h5>
                <Button
                  color={'negative'}
                  icon={GLOBAL_ICONS.delete}
                  isOutline
                  onClick={handleDelete}
                  disabled={!editMode}
                >
                  {t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.DELETE_GOAL')}
                </Button>
              </div>
            )}
          </form>
        );}}
    </Formik>
  );
};

export { AddEditGoalModal };
